import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { financeApi, Account } from '../../api/finance';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { IconActionButton } from '../../components/IconActionButton';
import { Power, Trash2 } from 'lucide-react';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  asset: 'Assets', liability: 'Liabilities', net_assets: 'Net Assets/Funds', income: 'Income',
  programme_expense: 'Programme Expenses', administrative_expense: 'Administrative Expenses',
  personnel_expense: 'Personnel Expenses', other_expense: 'Other Expenses', other_income: 'Other Income',
};

export function ChartOfAccountsPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const accountsQuery = useQuery({ queryKey: ['finance-accounts'], queryFn: () => financeApi.listAccounts() });
  const { register, handleSubmit, reset } = useForm<Record<string, string>>();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['finance-accounts'] });

  const createMutation = useMutation({
    mutationFn: (v: Record<string, string>) => financeApi.createAccount({
      accountCode: v.accountCode, accountName: v.accountName, accountType: v.accountType,
      parentAccountId: v.parentAccountId ? Number(v.parentAccountId) : undefined, normalBalance: v.normalBalance,
      description: v.description || undefined, postingAllowed: v.postingAllowed !== 'false',
    }),
    onSuccess: () => { showSuccess('Account created.'); invalidate(); setShowForm(false); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create account.'),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) => financeApi.setAccountActive(id, isActive),
    onSuccess: () => { showSuccess('Account updated.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => financeApi.deleteAccount(id),
    onSuccess: () => { showSuccess('Account deleted.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Cannot delete this account - it may have child accounts or existing transactions. Deactivate it instead.'),
  });

  const accounts = accountsQuery.data?.data ?? [];
  const filtered = accounts.filter((a) => {
    const matchesSearch = !search || a.account_code.includes(search) || a.account_name.toLowerCase().includes(search.toLowerCase());
    const matchesType = !typeFilter || a.account_type === typeFilter;
    return matchesSearch && matchesType;
  });

  return (
    <div className="page">
      <div className="page-header">
        <h1>Chart of Accounts</h1>
        <span className="row-actions">
          <a href={financeApi.chartOfAccountsPdfUrl()} target="_blank" rel="noreferrer">Download PDF</a>
          <button onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : 'Create Account'}</button>
        </span>
      </div>

      {showForm && (
        <section className="card">
          <h2>New Account</h2>
          <form onSubmit={handleSubmit((v) => createMutation.mutate(v))}>
            <label>Account Code<input {...register('accountCode', { required: true })} placeholder="e.g. 5110" /></label>
            <label>Account Name<input {...register('accountName', { required: true })} /></label>
            <label>
              Account Type
              <select {...register('accountType', { required: true })}>
                <option value="">- select -</option>
                {Object.entries(ACCOUNT_TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            <label>
              Parent Account (optional)
              <select {...register('parentAccountId')}>
                <option value="">- none (top-level) -</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}
              </select>
            </label>
            <label>
              Normal Balance
              <select {...register('normalBalance', { required: true })}>
                <option value="debit">Debit</option>
                <option value="credit">Credit</option>
              </select>
            </label>
            <label>Description<textarea rows={2} {...register('description')} /></label>
            <label><input type="checkbox" defaultChecked {...register('postingAllowed')} value="true" /> Posting allowed (uncheck for a summary/header account)</label>
            <div className="form-actions"><button type="submit" className="btn-primary" disabled={createMutation.isPending}>{createMutation.isPending ? 'Creating…' : 'Create'}</button><button type="button" className="btn-secondary" onClick={() => { setShowForm(false); reset(); }}>Cancel</button></div>
          </form>
        </section>
      )}

      <section className="card">
        <div className="inline-form">
          <input type="search" placeholder="Search by code or name" value={search} onChange={(e) => setSearch(e.target.value)} />
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">All Types</option>
            {Object.entries(ACCOUNT_TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <DataTable<Account>
          rows={filtered}
          rowKey={(a) => a.id}
          emptyMessage="No accounts match your search."
          columns={[
            { header: 'Code', render: (a) => a.account_code },
            { header: 'Name', render: (a) => a.account_name },
            { header: 'Type', render: (a) => ACCOUNT_TYPE_LABELS[a.account_type] ?? a.account_type },
            { header: 'Parent', render: (a) => a.parent_account_code ? `${a.parent_account_code} - ${a.parent_account_name}` : '-' },
            { header: 'Normal Balance', render: (a) => a.normal_balance },
            { header: 'Status', render: (a) => <StatusBadge status={a.is_active ? 'active' : 'inactive'} /> },
            {
              header: '',
              render: (a) => (
                <span className="icon-action-group">
                  {!a.is_system_account && (
                    <IconActionButton icon={Power} label={a.is_active ? 'Deactivate' : 'Activate'} onClick={() => toggleActiveMutation.mutate({ id: a.id, isActive: !a.is_active })} />
                  )}
                  {!a.is_system_account && (
                    <IconActionButton icon={Trash2} label="Delete" danger onClick={() => { if (window.confirm('Delete this account? This only works if it has no children or transactions.')) deleteMutation.mutate(a.id); }} />
                  )}
                </span>
              ),
            },
          ]}
        />
      </section>
    </div>
  );
}
